import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MediaManager from '../../components/ui/MediaManager';
import FileUpload from '../../components/ui/FileUpload';
import ImageCropper from '../../components/ui/ImageCropper';
import MediaGallery from '../../components/ui/MediaGallery';

interface MediaFile {
  id?: string;
  url: string;
  filename: string;
  size: number;
  mimetype: string;
  type?: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

const MediaDemo: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDemo, setSelectedDemo] = useState<string>('manager');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);

  const handleFilesChange = (files: MediaFile[]) => {
    setMediaFiles(files);
  };

  const handleUpload = (files: MediaFile[]) => {
    console.log('Uploaded files:', files);
    setMediaFiles(prev => [...prev, ...files]);
  };

  const handleCrop = (croppedImageUrl: string) => {
    console.log('Cropped image:', croppedImageUrl);
    // In a real app, you would upload the cropped image
    const newFile: MediaFile = {
      url: croppedImageUrl,
      filename: 'cropped-image.jpg',
      size: 0,
      mimetype: 'image/jpeg',
      uploadedAt: new Date().toISOString()
    };
    setMediaFiles(prev => [...prev, newFile]);
  };

  const handleDelete = (file: MediaFile) => {
    setMediaFiles(prev => prev.filter(f => f.url !== file.url));
  };

  const demoComponents = {
    manager: {
      title: 'Media Manager',
      description: 'Complete media management solution with upload, gallery, and cropping',
      component: (
        <MediaManager
          uploadType="image"
          allowMultiple={true}
          maxFiles={10}
          maxSize={5}
          onFilesChange={handleFilesChange}
          initialFiles={mediaFiles}
          showUpload={true}
          showGallery={true}
          showCropper={true}
        />
      )
    },
    upload: {
      title: 'File Upload',
      description: 'Drag and drop file upload with preview and validation',
      component: (
        <FileUpload
          accept="image/*"
          multiple={true}
          maxFiles={5}
          maxSize={5}
          uploadType="image"
          onUpload={handleUpload}
          onError={(error) => alert(error)}
          showPreview={true}
        />
      )
    },
    gallery: {
      title: 'Media Gallery',
      description: 'Display uploaded media with actions and viewer',
      component: (
        <MediaGallery
          files={mediaFiles}
          onDelete={handleDelete}
          showActions={true}
          showDetails={true}
          layout="grid"
          maxHeight="400px"
        />
      )
    },
    cropper: {
      title: 'Image Cropper',
      description: 'Crop and resize images with zoom and rotation',
      component: (
        <ImageCropper
          onCrop={handleCrop}
          onCancel={() => console.log('Crop cancelled')}
          aspectRatio={1}
          maxWidth={400}
          maxHeight={400}
        />
      )
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Media Components Demo</h1>
              <p className="text-sm text-gray-500">File upload and media management components</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Component Selector */}
        <div className="mb-8">
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
            {Object.entries(demoComponents).map(([key, demo]) => (
              <button
                key={key}
                onClick={() => setSelectedDemo(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedDemo === key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {demo.title}
              </button>
            ))}
          </div>
        </div>

        {/* Demo Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Component Demo */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {demoComponents[selectedDemo as keyof typeof demoComponents].title}
                </h2>
                <p className="text-gray-600">
                  {demoComponents[selectedDemo as keyof typeof demoComponents].description}
                </p>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                {demoComponents[selectedDemo as keyof typeof demoComponents].component}
              </div>
            </div>
          </div>

          {/* Info Panel */}
          <div className="space-y-6">
            {/* Features */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
              <div className="space-y-3">
                {selectedDemo === 'manager' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Complete Solution</p>
                        <p className="text-sm text-gray-600">Upload, gallery, and cropping in one component</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Multiple Views</p>
                        <p className="text-sm text-gray-600">Switch between upload, gallery, and cropper modes</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">File Management</p>
                        <p className="text-sm text-gray-600">Search, filter, and organize uploaded files</p>
                      </div>
                    </div>
                  </>
                )}

                {selectedDemo === 'upload' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Drag & Drop</p>
                        <p className="text-sm text-gray-600">Intuitive drag and drop interface</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">File Validation</p>
                        <p className="text-sm text-gray-600">Type and size validation with error handling</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Progress Tracking</p>
                        <p className="text-sm text-gray-600">Real-time upload progress and status</p>
                      </div>
                    </div>
                  </>
                )}

                {selectedDemo === 'gallery' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Grid & List Views</p>
                        <p className="text-sm text-gray-600">Switch between grid and list layouts</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Image Viewer</p>
                        <p className="text-sm text-gray-600">Full-screen image viewer with zoom and rotation</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">File Actions</p>
                        <p className="text-sm text-gray-600">Download, delete, and view file actions</p>
                      </div>
                    </div>
                  </>
                )}

                {selectedDemo === 'cropper' && (
                  <>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Interactive Cropping</p>
                        <p className="text-sm text-gray-600">Drag and resize crop area</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Zoom & Rotate</p>
                        <p className="text-sm text-gray-600">Zoom in/out and rotate images</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-gray-900">Aspect Ratio</p>
                        <p className="text-sm text-gray-600">Maintain aspect ratio for consistent crops</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Usage Stats */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Session</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Files Uploaded</span>
                  <span className="font-semibold text-gray-900">{mediaFiles.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Images</span>
                  <span className="font-semibold text-gray-900">
                    {mediaFiles.filter(f => f.mimetype.startsWith('image/')).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Documents</span>
                  <span className="font-semibold text-gray-900">
                    {mediaFiles.filter(f => !f.mimetype.startsWith('image/')).length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Size</span>
                  <span className="font-semibold text-gray-900">
                    {(mediaFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              </div>
            </div>

            {/* API Endpoints */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">API Endpoints</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-mono">POST</span>
                  <span className="font-mono text-gray-600">/api/upload/image</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-mono">POST</span>
                  <span className="font-mono text-gray-600">/api/upload/profile</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-mono">POST</span>
                  <span className="font-mono text-gray-600">/api/upload/document</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-mono">DEL</span>
                  <span className="font-mono text-gray-600">/api/upload/file/:type/:filename</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaDemo;